import { isNil } from 'lodash';
import { DateTime } from 'luxon';

import { getResources, getResourcesNew } from '../../framework/client/resource';
import { AccountType } from '../../../common/application/model/enums/AccountType';
import { Account } from '../../../common/application/model/Account';
import { Vendor } from '../../../common/application/model/Vendor';
import { Cache, cachedLoad, ValueLoadedToCacheCallback } from '../../framework/service/cache_service';
import { ApplicationResource } from '../../../common/application/enumeration/ApplicationResource';
import { getAccessibleCustomers, getVehicleContracts } from './resource_service';
import { VendorLocation } from '../../../common/application/model/VendorLocation';
import { sortAlphabetically } from './helper_service';
import { VatRate } from '../../../common/application/model/VatRate';
import { P_PAGE_SIZE } from '../../../common/framework/constants';

export interface Option {
    id: string | undefined;
    label: string;
}

const optionsCache: Cache<Option[]> = {
    values: new Map<string, Option[]>(),
    callbacks: new Map<string, Array<ValueLoadedToCacheCallback<Option[]>>>(),
};

export async function getCustomerOptions() {
    return [{ id: undefined as string | undefined, label: '' }]
        .concat(
            (await getAccessibleCustomers()).map((o) => {
                return { id: o.id, label: o.name };
            }),
        )
        .sort(sortAlphabetically('label'));
}

export async function getVendorOptions() {
    return (
        await cachedLoad(
            'vendor-options',
            async () => {
                return [{ id: undefined as string | undefined, label: '' }].concat(
                    (
                        (await getResourcesNew(
                            ApplicationResource.VENDOR,
                            -1,
                            new Map<string, string>([[P_PAGE_SIZE, '2000']]),
                        )) as Vendor[]
                    ).map((o) => {
                        return { id: o.id, label: o.name };
                    }),
                );
            },
            optionsCache,
        )
    ).sort(sortAlphabetically('label'));
}

export async function getAccountOptions(customerId: string | undefined | null, accountType: AccountType) {
    return await cachedLoad(
        'account-options-for-customer-' + customerId + '-' + accountType,
        async () => {
            const parameters = new Map<string, string>();
            if (customerId === 'ALL' || isNil(customerId)) {
                parameters.set('customerId', 'ALL');
            }
            if (customerId === 'NULL') {
                parameters.set('customerId', 'NULL');
            } else {
                parameters.set('customerId', customerId || '');
            }

            parameters.set('accountType', accountType);
            parameters.set(P_PAGE_SIZE, '2000');
            return [{ id: undefined as string | undefined, label: '' }].concat(
                ((await getResourcesNew(ApplicationResource.ACCOUNT, -1, parameters)) as Account[]).map((o) => {
                    return { id: o.id, label: o.code + ' : ' + o.description };
                }),
            );
        },
        optionsCache,
    );
}

export async function getVehicleContractOptions(customerIds: string[]) {
    return await cachedLoad(
        'vehicle-contract-options-for-customer-' + customerIds,
        async () => {
            return [{ id: undefined as string | undefined, label: '' }].concat(
                (await getVehicleContracts(customerIds)).map((o) => {
                    return { id: o.id, label: o.vehicleRegistrationNumber ?? '-' };
                }),
            );
        },
        optionsCache,
    );
}

export async function getVendorLocationOptions() {
    return await cachedLoad(
        'vendor-location-options',
        async () => {
            return [{ id: undefined as string | undefined, label: '' }].concat(
                ((await getResourcesNew(ApplicationResource.VENDOR_LOCATION, -1)) as VendorLocation[]).map((o) => {
                    return { id: o.id, label: o.name };
                }),
            );
        },
        optionsCache,
    );
}

export async function getVatRateOptions(countryCode: string) {
    const params = new Map();
    params.set('countryCode', countryCode);
    params.set('date', DateTime.now().toFormat('yyyy-MM-dd'));
    params.set('query', `countryCode = {countryCode} AND startDate <= {date} AND (endDate >= {date} OR endDate IS NULL)`);

    return [{ id: undefined as string | undefined, label: '-' }].concat(
        (await getResources<VatRate>('vat-rate', -1, params)).map((o) => {
            return { id: o.vatCode, label: `${o.vatCode} (${o.vatPercentage} %)` };
        }),
    );
}
